import type { useApiFetch } from '@/features/auth'
import type { CaseStatus, ServiceType } from '@/features/cases'
import { buildQueryString } from '@/lib/buildQueryString'

/** `GET /admin/overview` query params. `serviceType` narrows only `casesByStatus` — every other
 * field on the response is always computed globally. */
export interface AdminOverviewParams {
  serviceType?: ServiceType
}

export interface AdminServiceTypeCount {
  serviceType: ServiceType
  count: number
}

export interface AdminCaseStatusCount {
  status: CaseStatus
  count: number
}

export interface AdminRecentCase {
  id: string
  reference: string
  serviceType: ServiceType
  caseStatus: CaseStatus
  investorId: string
  investorName: string | null
  advisorName: string | null
  createdAt: string
}

/** Mirrors `AdminOverviewDto`. `activeCases` = count of cases where `caseStatus !== 'published'`;
 * `publishedThisMonth` is calendar-month-to-date, not a rolling 30 days; `leadsByType`/
 * `casesByStatus` are zero-filled across every `ServiceType`/`CaseStatus` value. */
export interface AdminOverview {
  totalInvestors: number
  activeCases: number
  awaitingReview: number
  unassignedCount: number
  publishedThisMonth: number
  leadsByType: AdminServiceTypeCount[]
  casesByStatus: AdminCaseStatusCount[]
  recentCases: AdminRecentCase[]
}

type AuthedFetch = ReturnType<typeof useApiFetch>

export function fetchAdminOverview(
  authedFetch: AuthedFetch,
  params: AdminOverviewParams = {},
): Promise<AdminOverview> {
  return authedFetch<AdminOverview>(`/api/v1/admin/overview${buildQueryString(params)}`)
}
